import { debugAtom, debugStore } from "@folio-2026/experience"
import Camera from "@folio-2026/section/camera"
import { EVENTS, lerp, MQ } from "@folio-2026/shared"
import assert from "assert"
import gsap from "gsap"
import * as THREE from "three/webgpu"

import Memories from "./Memories"
import { cameraMovements } from "./const"

const mm = gsap.matchMedia()

export default class GuestbookSectionCamera extends Camera {
  pos: THREE.Vector3
  look: THREE.Vector3
  currentLook: THREE.Vector3

  private readonly maxRotationX = THREE.MathUtils.degToRad(30) // Math.PI / 6 // 30 degrees
  private readonly maxRotationY = THREE.MathUtils.degToRad(30) // Math.PI / 6 // 30 degrees
  private offsetX = THREE.MathUtils.degToRad(0)
  private offsetY = THREE.MathUtils.degToRad(10)

  private targetRotation = { x: 0, y: 0 }
  private currentRotation = { x: 0, y: 0 }

  private isOrbitControls: boolean = false

  enteringAnimation: gsap.core.Timeline
  scrollingAnimation: gsap.core.Timeline

  private originalPos?: THREE.Vector3
  private originalLook?: THREE.Vector3
  private memoryClickAnimation?: gsap.core.Timeline
  private touchStartY = 0
  private touchStartTime = 0
  private wheelHandler?: () => void
  private touchStartHandler?: (e: TouchEvent) => void
  private touchMoveHandler?: (e: TouchEvent) => void

  constructor({ scene }: { scene: THREE.Scene }) {
    super({ scene })

    const camMov = cameraMovements.get("initial")
    this.pos = new THREE.Vector3(
      camMov.positionX,
      camMov.positionY,
      camMov.positionZ,
    )
    this.look = new THREE.Vector3(
      camMov.lookAtX,
      camMov.lookAtY,
      camMov.lookAtZ,
    )
    this.currentLook = new THREE.Vector3(
      camMov.lookAtX,
      camMov.lookAtY,
      camMov.lookAtZ,
    )

    this.enteringAnimation = this.createEnteringAnimation()
    this.scrollingAnimation = this.createScrollingAnimation()

    this.setDebug()
    this.listenEvents()
  }

  listenEvents() {
    this.experience.on(EVENTS.Guestbook.BeforeEnter, () => {
      this.offsetY = THREE.MathUtils.degToRad(0)
    })

    this.experience.on(
      EVENTS.Guestbook.Entering,
      this.enterAnimation.bind(this),
    )

    this.experience.on(EVENTS.Guestbook.Scroll, this.onScroll.bind(this))

    this.experience.on(
      EVENTS.Guestbook.Memory.Click,
      this.onMemoryClick.bind(this),
    )
  }

  onMemoryClick(event: unknown) {
    const { index, memory, memoriesInstance } = event as {
      index: number
      memory: [number, number, number]
      memoriesInstance: Memories
    }

    if (this.originalPos && this.originalLook) {
      return
    }

    // Kill any existing memory click animation and clean up event listeners
    if (this.memoryClickAnimation) {
      this.cleanupScrollEventListeners()
      this.memoryClickAnimation.kill()
    }

    // Save original position and look target before animation
    if (!this.originalPos) {
      this.originalPos = new THREE.Vector3()
    }
    if (!this.originalLook) {
      this.originalLook = new THREE.Vector3()
    }
    this.originalPos.copy(this.pos)
    this.originalLook.copy(this.look)

    // Calculate target position: 2 units in front of memory along Z-axis
    const [memoryX, memoryY, memoryZ] = memory
    const targetPosX = memoryX
    const targetPosY = memoryY
    const targetPosZ = memoryZ + 0.25
    const targetPosZ_mobile = memoryZ + 0.4

    const targetLookX = memoryX
    const targetLookY = memoryY
    const targetLookZ = memoryZ

    // Create bound handlers for proper cleanup
    this.wheelHandler = this.handleScrollGesture.bind(this)
    this.touchStartHandler = this.onTouchStart.bind(this)
    this.touchMoveHandler = this.onTouchMove.bind(this)

    const domElement = this.experience.renderer.instance.domElement

    const tl = gsap.timeline({
      onStart: () => {
        this.experience.lenis?.stop()
        // Add wheel event for desktop (cross-browser compatible)
        domElement.addEventListener("wheel", this.wheelHandler!, {
          passive: true,
        })
        // Add touch events for mobile
        domElement.addEventListener("touchstart", this.touchStartHandler!, {
          passive: true,
        })
        domElement.addEventListener("touchmove", this.touchMoveHandler!, {
          passive: true,
        })

        //
        // Reset progress before revealing
        memoriesInstance.messageText.progressUniform.value = 0
      },
      onReverseComplete: () => {
        this.experience.lenis?.start()
        this.cleanupScrollEventListeners()
        this.originalPos = undefined
        this.originalLook = undefined
        // Resume hover detection when exiting memory view
        if (memoriesInstance) {
          memoriesInstance.isInMemoryView = false
        }
      },
    })

    mm.add(
      {
        lgUp: MQ.lgUp,
        lgDown: MQ.lgDown,
      },
      (context) => {
        const { lgUp } = context.conditions as { lgUp: boolean }

        const offsetProxy = { value: this.offsetY }

        tl.to(this.pos, {
          duration: 3,
          ease: "power3.inOut",
          x: targetPosX,
          y: targetPosY,
          z: lgUp ? targetPosZ : targetPosZ_mobile,
        })
          .to(
            this.look,
            {
              duration: 3,
              ease: "power3.inOut",
              x: targetLookX,
              y: targetLookY,
              z: targetLookZ,
            },
            0,
          )
          .to(
            offsetProxy,
            {
              duration: 3,
              ease: "power3.inOut",
              value: 0,
              onUpdate: () => {
                this.offsetY = offsetProxy.value
              },
            },
            0,
          )
          .to(
            memoriesInstance.settings.paperEmissiveIntensity,
            {
              value: 0.07,
              duration: 3,
              ease: "power3.inOut",
            },
            0,
          )
          .call(
            () => {
              if (tl.reversed()) {
                memoriesInstance.messageText.leave()
                this.experience.trigger(EVENTS.Guestbook.Memory.Close, [event])
              } else {
                memoriesInstance.updateMessageText({
                  index: index,
                  memory: memory,
                })
                memoriesInstance.messageText.reveal()
              }
            },
            undefined,
            2,
          )
      },
    )
    this.memoryClickAnimation = tl
  }

  revertMemoryClickAnimation() {
    if (this.originalPos && this.originalLook && this.memoryClickAnimation) {
      this.memoryClickAnimation?.pause().reverse()
    }
  }

  private handleScrollGesture() {
    this.revertMemoryClickAnimation()
  }

  private cleanupScrollEventListeners() {
    const domElement = this.experience.renderer.instance.domElement

    if (this.wheelHandler) {
      domElement.removeEventListener("wheel", this.wheelHandler)
      this.wheelHandler = undefined
    }

    if (this.touchStartHandler) {
      domElement.removeEventListener("touchstart", this.touchStartHandler)
      this.touchStartHandler = undefined
    }

    if (this.touchMoveHandler) {
      domElement.removeEventListener("touchmove", this.touchMoveHandler)
      this.touchMoveHandler = undefined
    }

    // Reset touch tracking
    this.touchStartY = 0
    this.touchStartTime = 0
  }

  private onTouchStart(e: TouchEvent) {
    if (e.touches.length === 1) {
      this.touchStartY = e.touches[0].clientY
      this.touchStartTime = Date.now()
    }
  }

  private onTouchMove(e: TouchEvent) {
    if (e.touches.length === 1 && this.touchStartY !== 0) {
      const touchY = e.touches[0].clientY
      const deltaY = Math.abs(touchY - this.touchStartY)
      const deltaTime = Date.now() - this.touchStartTime

      // Detect scroll gesture: significant vertical movement within short time
      if (deltaY > 10 && deltaTime < 300) {
        this.handleScrollGesture()
        this.touchStartY = 0
        this.touchStartTime = 0
      }
    }
  }

  enterAnimation(event: unknown) {
    assert(this.enteringAnimation, "Works entering animation is not defined")

    const { progress } = event as { progress: number }

    this.enteringAnimation.seek(progress)

    // Manually update offsetY based on progress since seek() doesn't call onUpdate
    // The offset animation starts at 0.4 and lasts 0.6 seconds (ends at 1.0)
    if (progress >= 0.4) {
      const offsetProgress = Math.min(1, (progress - 0.4) / 0.6)
      const ease = gsap.parseEase("expo.in")
      const easedProgress = ease(offsetProgress)
      this.offsetY = THREE.MathUtils.lerp(
        THREE.MathUtils.degToRad(0),
        THREE.MathUtils.degToRad(10),
        easedProgress,
      )
    } else {
      this.offsetY = THREE.MathUtils.degToRad(0)
    }
  }

  onScroll(event: unknown) {
    const { progress } = event as { progress: number }

    // Normal scroll behavior
    this.scrollingAnimation.seek(progress)
  }

  createScrollingAnimation() {
    const camMov = cameraMovements.get("scroll-end")

    const tl = gsap.timeline({
      paused: true,
    })

    tl.to(this.pos, {
      duration: 1,
      // ease: "power3.in",
      x: camMov.positionX,
      y: camMov.positionY,
      z: camMov.positionZ,
    }).to(
      this.look,
      {
        duration: 1,
        // ease: "power3.in",
        x: camMov.lookAtX,
        y: camMov.lookAtY,
        z: camMov.lookAtZ,
      },
      0,
    )

    return tl
  }

  createEnteringAnimation() {
    const camMov1 = cameraMovements.get("entering-t1")
    const camMov2 = cameraMovements.get("entering-t2")
    const camMov3 = cameraMovements.get("entering-t3")

    const tl = gsap.timeline({
      paused: true,
      duration: 1,
    })

    const tlPos = gsap
      .timeline()
      .to(this.pos, {
        duration: 0.2,
        x: camMov1.positionX,
        y: camMov1.positionY,
        z: camMov1.positionZ,
      })
      .to(this.pos, {
        duration: 0.2,
        x: camMov2.positionX,
        y: camMov2.positionY,
        z: camMov2.positionZ,
      })
      .to(this.pos, {
        duration: 0.6,
        x: camMov3.positionX,
        y: camMov3.positionY,
        z: camMov3.positionZ,
      })

    const tlLook = gsap
      .timeline()
      .to(this.look, {
        duration: 0.2,
        x: camMov1.lookAtX,
        y: camMov1.lookAtY,
        z: camMov1.lookAtZ,
      })
      .to(this.look, {
        duration: 0.2,
        x: camMov2.lookAtX,
        y: camMov2.lookAtY,
        z: camMov2.lookAtZ,
      })
      .to(this.look, {
        duration: 0.6,
        ease: "expo.in",
        x: camMov3.lookAtX,
        y: camMov3.lookAtY,
        z: camMov3.lookAtZ,
      })

    // Note: offsetY is updated manually in enterAnimation() since seek() doesn't call onUpdate
    const offsetProxy = { value: this.offsetY }
    tl.to(
      offsetProxy,
      {
        duration: 0.6,
        ease: "expo.in",
        value: THREE.MathUtils.degToRad(10),
      },
      0.4,
    )

    tl.add(tlPos, 0).add(tlLook, 0)

    return tl
  }

  update() {
    super.update()

    if (this.isOrbitControls) {
      return
    }

    this.updateCameraPosition()
    this.lerpPosition()
    this.calculateTargetRotation()
    this.lerpRotation()
    this.updateCameraLookAt()
  }

  private updateCameraPosition() {
    this.camera.position.set(this.pos.x, this.pos.y, this.pos.z)
  }

  private calculateTargetRotation() {
    // Calculate target rotation based on mouse position
    // Inverted horizontal: mouse left = rotate right (to see left side)
    // Normal vertical: mouse up = rotate up (to see upwards)
    this.targetRotation.y = -this.currentMouseCoords.x * this.mouseSensitivityX
    this.targetRotation.x = this.currentMouseCoords.y * this.mouseSensitivityY

    // Clamp rotation to max values
    this.targetRotation.x = Math.max(
      -this.maxRotationX,
      Math.min(this.maxRotationX, this.targetRotation.x),
    )
    this.targetRotation.y = Math.max(
      -this.maxRotationY,
      Math.min(this.maxRotationY, this.targetRotation.y),
    )
  }

  private lerpRotation() {
    this.currentRotation.x = lerp(
      this.currentRotation.x,
      this.targetRotation.x,
      this.lerpSpeed,
    )
    this.currentRotation.y = lerp(
      this.currentRotation.y,
      this.targetRotation.y,
      this.lerpSpeed,
    )
  }

  private updateCameraLookAt() {
    // Calculate direction from position to look target
    const direction = new THREE.Vector3()
      .subVectors(this.look, this.pos)
      .normalize()

    // Calculate base rotation from direction using spherical coordinates
    // This avoids the flip issue that lookAt() can cause
    const spherical = new THREE.Spherical()
    spherical.setFromVector3(direction)

    // Apply mouse rotation offsets
    const finalTheta = spherical.theta + this.currentRotation.y + this.offsetY
    const finalPhi = spherical.phi + this.currentRotation.x + this.offsetX

    // Clamp phi to prevent gimbal lock
    const clampedPhi = Math.max(0.1, Math.min(Math.PI - 0.1, finalPhi))

    // Convert back to direction vector with mouse rotation applied
    spherical.set(spherical.radius, clampedPhi, finalTheta)
    const finalDirection = new THREE.Vector3().setFromSpherical(spherical)

    // Set camera to look at the modified direction
    const targetPoint = new THREE.Vector3().addVectors(
      this.camera.position,
      finalDirection,
    )
    this.camera.lookAt(targetPoint)
  }

  setDebug() {
    debugStore.sub(debugAtom, () => {
      const state = debugStore.get(debugAtom)

      this.isOrbitControls = state.showOrbitControls
    })
  }
}
